import React from 'react'
import { River, Heading, Text } from '@primer/react-brand'
import Container from './Container'
import Eyebrow from './landing/Eyebrow'

// The prospectus's "platform beneath an ecosystem" point: for the most
// sophisticated organisations, Civic Lens is the base layer of a larger stack.
// The visual is that stack, drawn as three labelled layers — true, and nothing
// to fabricate. Everything in it is a token, so it holds in either theme.
const LAYERS = [
  { label: 'Your products and reporting', sub: 'dashboards · briefs · published work', tone: 'top' },
  { label: 'Your analysis', sub: 'labelling · models · your own tools', tone: 'mid' },
  { label: 'CivicLens', sub: 'collection · structure · search · API · MCP · CSV', tone: 'base' },
]

const Stack = () => (
  <div
    role="img"
    aria-label="CivicLens as the base layer beneath your own analysis and your own products"
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
      // River.Visual does not inherit the body face; without this the layers
      // fall back to the browser serif.
      fontFamily: 'var(--brand-fontStack-sansSerif)',
    }}
  >
    {LAYERS.map(({ label, sub, tone }, i) => {
      const isBase = tone === 'base'
      return (
        <div
          key={label}
          style={{
            // Each layer slightly wider than the one above it, so it reads as a
            // foundation rather than a list.
            marginInline: `${(2 - i) * 24}px`,
            padding: '18px 22px',
            borderRadius: '10px',
            border: `1px solid ${isBase ? 'var(--base-color-scale-blue-5)' : 'var(--brand-color-border-default)'}`,
            background: isBase ? 'var(--base-color-scale-blue-5)' : 'var(--brand-color-canvas-default)',
            color: isBase ? 'var(--brand-color-text-onEmphasis)' : 'var(--brand-color-text-default)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.3 }}>{label}</div>
          <div
            style={{
              marginTop: '4px',
              fontFamily: 'var(--brand-fontStack-monospace)',
              fontSize: '12px',
              letterSpacing: '0.04em',
              opacity: isBase ? 0.85 : 1,
              color: isBase ? 'inherit' : 'var(--brand-color-text-muted)',
            }}
          >
            {sub}
          </div>
        </div>
      )
    })}
  </div>
)

const EcosystemSection = () => {
  return (
    <section style={{ padding: '96px 0', background: 'var(--brand-color-canvas-default)' }}>
      <Container>
        <River align="end" imageTextRatio="50:50">
          <River.Visual hasShadow={false}>
            <Stack />
          </River.Visual>
          <River.Content>
            <Eyebrow>Built to be built on</Eyebrow>
            <Heading as="h2" size="4" weight="semibold" style={{ marginTop: '12px' }}>
              A platform beneath an ecosystem
            </Heading>
            <Text as="p" size="300" variant="muted" style={{ marginTop: '16px' }}>
              CivicLens is not only an end-user tool. For several of the most sophisticated organisations
              in the field it is the base layer of a larger stack: CivicLens collects and structures the
              raw social data, a research partner enriches it with post-level analysis, and a third team
              builds the stakeholder-facing products on top.
            </Text>
            <Text as="p" size="300" variant="muted" style={{ marginTop: '12px' }}>
              That only works if the base layer is reliable, current and complete — which is why the data
              leaves through open doors: CSV, a REST API, and an MCP server your own agents can call.
            </Text>
          </River.Content>
        </River>
      </Container>
    </section>
  )
}

export default EcosystemSection
